import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { analyticsAPI } from '../../lib/api';
import api from '../../lib/api';
import { 
  TrendingUp, 
  DollarSign, 
  Building2, 
  Users, 
  UserPlus,
  Calendar,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  PieChart,
  BarChart3,
  Loader2,
  GitCompare,
  ZoomIn
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import DrillDownModal from '../../components/DrillDownModal';
import { logger } from '../../lib/logger';

// Simple chart component using CSS
const SimpleBarChart = ({ data, dataKey, label, color = '#DC2626' }) => {
  if (!data || data.length === 0) return <div className="text-center text-slate-400 py-8">No data available</div>;
  
  const maxValue = Math.max(...data.map(d => d[dataKey] || 0));
  
  return (
    <div className="space-y-2">
      {data.slice(-14).map((item, index) => (
        <div key={item.date || item.label} className="flex items-center gap-2 text-sm">
          <span className="w-20 text-slate-500 text-xs">{item.date?.slice(5) || index}</span>
          <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${maxValue > 0 ? (item[dataKey] / maxValue) * 100 : 0}%`,
                backgroundColor: color 
              }}
            />
          </div>
          <span className="w-16 text-right font-medium text-slate-700">
            {typeof item[dataKey] === 'number' ? item[dataKey].toLocaleString() : item[dataKey]}
          </span>
        </div>
      ))}
    </div>
  );
};

// Line chart component for trends
const SimpleLineChart = ({ data, dataKey, label, color = '#DC2626', valuePrefix = '' }) => {
  if (!data || data.length === 0) return <div className="text-center text-slate-400 py-8">No data available</div>;
  
  const values = data.map(d => d[dataKey] || 0);
  const maxValue = Math.max(...values, 1);
  const minValue = Math.min(...values, 0);
  const range = maxValue - minValue || 1;
  
  const width = 100;
  const height = 60;
  const padding = 5;
  
  // Generate SVG path
  const points = data.map((item, index) => {
    const x = (index / (data.length - 1 || 1)) * (width - 2 * padding) + padding;
    const y = height - padding - ((item[dataKey] - minValue) / range) * (height - 2 * padding);
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <div className="space-y-3">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(percent => (
          <line
            key={percent}
            x1={padding}
            y1={height - padding - (percent / 100) * (height - 2 * padding)}
            x2={width - padding}
            y2={height - padding - (percent / 100) * (height - 2 * padding)}
            stroke="#e2e8f0"
            strokeWidth="0.2"
          />
        ))}
        
        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Area under line */}
        <polygon
          points={`${padding},${height - padding} ${points} ${width - padding},${height - padding}`}
          fill={color}
          opacity="0.1"
        />
        
        {/* Data points */}
        {data.map((item, index) => {
          const x = (index / (data.length - 1 || 1)) * (width - 2 * padding) + padding;
          const y = height - padding - ((item[dataKey] - minValue) / range) * (height - 2 * padding);
          return (
            <circle
              key={item.date || item.label || item[dataKey]}
              cx={x}
              cy={y}
              r="1"
              fill={color}
            />
          );
        })}
      </svg>
      
      {/* Labels */}
      <div className="flex justify-between text-xs text-slate-400">
        <span>{data[0]?.date?.slice(5, 10) || 'Start'}</span>
        <span>{data[data.length - 1]?.date?.slice(5, 10) || 'End'}</span>
      </div>
    </div>
  );
};

// Pie chart component
const SimplePieChart = ({ data, colors }) => {
  if (!data || Object.keys(data).length === 0) return <div className="text-center text-slate-400 py-4">No data</div>;
  
  const total = Object.values(data).reduce((sum, val) => sum + val, 0);
  const entries = Object.entries(data).filter(([_, val]) => val > 0);
  
  return (
    <div className="space-y-2">
      {entries.map(([key, value], index) => (
        <div key={key} className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: colors[index % colors.length] }}
            />
            <span className="text-sm capitalize">{key || 'None'}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium">{value}</span>
            <span className="text-slate-400 ml-1">({total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function PlatformAnalytics() {
  const { user, isPlatformAdmin } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [drillDownModal, setDrillDownModal] = useState({ isOpen: false, data: null, title: '', type: '' });
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonAnalytics, setComparisonAnalytics] = useState(null);
  const [comparisonDateRange, setComparisonDateRange] = useState('30d');
  const [comparisonCustomStart, setComparisonCustomStart] = useState('');
  const [comparisonCustomEnd, setComparisonCustomEnd] = useState('');

  const getDateRange = useCallback(() => {
    const now = new Date();
    let start, end;
    end = now.toISOString();
    
    switch (dateRange) {
      case '7d':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '30d':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '90d':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '1y':
        start = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'custom':
        start = customStart ? new Date(customStart).toISOString() : new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        end = customEnd ? new Date(customEnd).toISOString() : now.toISOString();
        break;
      default:
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    }
    
    return { start, end };
  }, [dateRange, customStart, customEnd]);

  const fetchAnalytics = useCallback(async () => {
    if (!isPlatformAdmin()) return;
    
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const res = await analyticsAPI.getAnalytics(start, end);
      setAnalytics(res.data);
    } catch (error) {
      logger.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin, getDateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const { start, end } = getDateRange();
      const response = await analyticsAPI.exportCSV(start, end);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_report_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV report exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const { start, end } = getDateRange();
      const response = await api.get('/platform/analytics/export-pdf', {
        params: { start_date: start, end_date: end },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `platform_analytics_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF report');
    } finally {
      setExporting(false);
    }
  };

  if (!isPlatformAdmin()) {
    return (
      <div className="p-8 text-center" data-testid="platform-analytics-unauthorized">
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 mt-2">Platform admin access required to view analytics.</p>
      </div>
    );
  }

  const pieColors = ['#DC2626', '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6'];

  return (
    <div className="p-6 space-y-6" data-testid="platform-analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Platform Analytics</h1>
          <p className="text-slate-500">Comprehensive overview of platform performance</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Date Range Selector */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]" data-testid="date-range-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>
          
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="w-[140px]"
                data-testid="custom-start-date"
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="w-[140px]"
                data-testid="custom-end-date"
              />
              <Button size="sm" onClick={fetchAnalytics} data-testid="apply-date-range">
                Apply
              </Button>
            </div>
          )}
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchAnalytics}
            disabled={loading}
            data-testid="refresh-analytics"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button 
            variant={comparisonMode ? "default" : "outline"}
            size="sm" 
            onClick={() => setComparisonMode(!comparisonMode)}
            className={comparisonMode ? "bg-purple-500 hover:bg-purple-600" : ""}
          >
            <GitCompare className="w-4 h-4 mr-2" />
            {comparisonMode ? 'Exit Compare' : 'Compare'}
          </Button>
          
          <Button 
            size="sm" 
            onClick={handleExportCSV}
            disabled={exporting}
            variant="outline"
            data-testid="export-csv"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            CSV
          </Button>
          
          <Button 
            size="sm" 
            onClick={handleExportPDF}
            disabled={exporting}
            className="bg-red-500 hover:bg-red-600"
            data-testid="export-pdf"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      ) : analytics ? (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card data-testid="metric-total-revenue">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Revenue</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.revenue.total_formatted}</p>
                    <p className="text-xs text-slate-400 mt-1">{analytics.revenue.transactions} transactions</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="metric-mrr">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Monthly Recurring Revenue</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.revenue.mrr_formatted}</p>
                    <p className="text-xs text-slate-400 mt-1">MRR estimate</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="metric-institutions">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Institutions</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.institutions.total}</p>
                    <p className="text-xs text-green-600 mt-1 flex items-center">
                      <ArrowUpRight className="w-3 h-3 mr-1" />
                      +{analytics.institutions.new_in_period} in period
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="metric-conversion">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Conversion Rate</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.institutions.conversion_rate}%</p>
                    <p className="text-xs text-slate-400 mt-1">Trial to Paid</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Activity className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Active Users</p>
                    <p className="text-xl font-bold text-slate-900">{analytics.users.total_active.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Patients</p>
                    <p className="text-xl font-bold text-slate-900">{analytics.patients.total.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Avg Revenue/Transaction</p>
                    <p className="text-xl font-bold text-slate-900">
                      ₹{analytics.revenue.transactions > 0 
                        ? Math.round(analytics.revenue.total / analytics.revenue.transactions).toLocaleString() 
                        : 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trend Charts - Line Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Growth Trend */}
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setDrillDownModal({ isOpen: true, data: analytics.revenue.daily_data, title: 'Revenue Breakdown', type: 'revenue_daily' })}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Revenue Growth Trend
                  <ZoomIn className="w-4 h-4 ml-auto text-slate-400" />
                </CardTitle>
                <CardDescription>Revenue progression over time (click for details)</CardDescription>
              </CardHeader>
              <CardContent>
                <SimpleLineChart 
                  data={analytics.revenue.daily_data} 
                  dataKey="revenue" 
                  label="Revenue" 
                  color="#22C55E"
                  valuePrefix="₹"
                />
                {analytics.revenue.daily_data.length > 1 && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-green-800 font-medium">Trend Analysis</span>
                      <span className="text-green-600">
                        {analytics.revenue.daily_data[analytics.revenue.daily_data.length - 1]?.revenue > 
                         analytics.revenue.daily_data[0]?.revenue ? '↑ Increasing' : '↓ Decreasing'}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* User Growth Trend */}
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setDrillDownModal({ isOpen: true, data: analytics.users.growth_data, title: 'User Growth Breakdown', type: 'users_growth' })}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  User Growth Trend
                  <ZoomIn className="w-4 h-4 ml-auto text-slate-400" />
                </CardTitle>
                <CardDescription>New user registrations over time (click for details)</CardDescription>
              </CardHeader>
              <CardContent>
                <SimpleLineChart 
                  data={analytics.users.growth_data} 
                  dataKey="new_users" 
                  label="Users" 
                  color="#3B82F6"
                />
                {analytics.users.growth_data.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-blue-600 font-medium">Peak Day</span>
                        <p className="text-blue-800 mt-1">
                          {Math.max(...analytics.users.growth_data.map(d => d.new_users))} users
                        </p>
                      </div>
                      <div>
                        <span className="text-blue-600 font-medium">Daily Average</span>
                        <p className="text-blue-800 mt-1">
                          {Math.round(analytics.users.growth_data.reduce((sum, d) => sum + d.new_users, 0) / 
                           analytics.users.growth_data.length)} users
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Revenue Trend
                </CardTitle>
                <CardDescription>Daily revenue over selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={analytics.revenue.daily_data} 
                  dataKey="revenue" 
                  label="Revenue" 
                  color="#22C55E"
                />
              </CardContent>
            </Card>

            {/* Patient Registrations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-500" />
                  Patient Registrations
                </CardTitle>
                <CardDescription>Daily new patients over selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={analytics.patients.growth_data} 
                  dataKey="new_patients" 
                  label="Patients" 
                  color="#3B82F6"
                />
              </CardContent>
            </Card>

            {/* Institutions by Plan */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-purple-500" />
                  Institutions by Plan
                </CardTitle>
                <CardDescription>Distribution across subscription plans</CardDescription>
              </CardHeader>
              <CardContent>
                <SimplePieChart data={analytics.institutions.by_plan} colors={pieColors} />
              </CardContent>
            </Card>

            {/* Institutions by Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-amber-500" />
                  Institutions by Status
                </CardTitle>
                <CardDescription>Subscription status distribution</CardDescription>
              </CardHeader>
              <CardContent>
                <SimplePieChart data={analytics.institutions.by_status} colors={['#22C55E', '#EAB308', '#EF4444', '#6B7280']} />
              </CardContent>
            </Card>
          </div>

          {/* Top Institutions Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-red-500" />
                Top Institutions by Patient Registrations
              </CardTitle>
              <CardDescription>Most active institutions in selected period</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.institutions.top_by_patients.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Rank</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Institution</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Patients Registered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.institutions.top_by_patients.map((inst, index) => (
                        <tr key={inst.institution_id || index} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' :
                              index === 1 ? 'bg-slate-200 text-slate-700' :
                              index === 2 ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-500'
                            }`}>
                              {index + 1}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-medium text-slate-800">{inst.name}</td>
                          <td className="py-3 px-4 text-right">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {inst.patients.toLocaleString()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-slate-400 py-8">No institution activity in selected period</p>
              )}
            </CardContent>
          </Card>

          {/* User Growth */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                User Growth
              </CardTitle>
              <CardDescription>New user registrations over selected period</CardDescription>
            </CardHeader>
            <CardContent>
              <SimpleBarChart 
                data={analytics.users.growth_data} 
                dataKey="new_users" 
                label="Users" 
                color="#6366F1"
              />
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-20 text-slate-500">
          No analytics data available
        </div>
      )}
      
      {/* Drill-Down Modal */}
      <DrillDownModal
        isOpen={drillDownModal.isOpen}
        onClose={() => setDrillDownModal({ isOpen: false, data: null, title: '', type: '' })}
        data={drillDownModal.data}
        title={drillDownModal.title}
        type={drillDownModal.type}
      />
    </div>
  );
}
